import { useEffect } from "react";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { Link } from "@/components/LLink";
import { Reveal } from "@/components/motion";
import ContactForm from "@/components/ContactForm";
import SEO from "@/components/SEO";
import { useLang } from "@/i18n";
import { GATES } from "@/seo/gatesContent";

function useSchema(c) {
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify([
      { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: c.faq.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
      { "@context": "https://schema.org", "@type": "Product", name: c.seo[0].split(" | ")[0], description: c.seo[1], image: `${window.location.origin}/images/porti-hero.jpg`, brand: { "@type": "Brand", name: "HEAVY METAL" }, offers: { "@type": "Offer", availability: "https://schema.org/InStock", priceCurrency: "RON", price: "0", url: window.location.href } },
    ]);
    document.head.appendChild(el);
    return () => el.remove();
  }, [c]);
}

const H2 = "mt-5 font-heading text-3xl font-semibold tracking-tight md:text-[40px]";
const WIDE = "px-4 py-24 sm:px-6 lg:px-10 2xl:px-16";
const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

export default function GatesPage({ product }) {
  const { lang } = useLang();
  const c = GATES[lang] || GATES.ro;
  useSchema(c);

  return (
    <div data-testid="gates-page">
      <SEO metaKey="porti-garduri" title={c.seo[0]} description={c.seo[1]} image="/images/porti-hero.jpg" type="product" />

      <section className="relative overflow-hidden bg-[#0b1220] text-white" data-testid="gates-hero">
        <div className="grid lg:grid-cols-12 lg:min-h-[660px]">
          <div className="relative z-10 flex flex-col justify-end px-4 pb-16 pt-36 sm:px-6 lg:col-span-6 lg:px-10 lg:pb-20 2xl:px-16">
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-white/60">{c.kicker}</p>
            <h1 className="mt-6 max-w-2xl font-heading text-4xl font-semibold leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl" data-testid="gates-title">{c.title}</h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/70 md:text-lg">{c.sub}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => scrollTo("gates-form")} data-testid="gates-hero-cta" className="inline-flex items-center gap-2 bg-white px-6 py-3.5 text-[13px] font-semibold text-ink transition-colors hover:bg-gold">
                {c.cta_k} <ArrowRight className="h-4 w-4" />
              </button>
              <button onClick={() => scrollTo("gates-dev")} data-testid="gates-hero-dev" className="inline-flex items-center gap-2 border border-white/30 px-6 py-3.5 text-[13px] font-semibold text-white transition-colors hover:border-white">
                {c.dev_k} <ArrowUpRight className="h-4 w-4" />
              </button>
            </div>
            <ul className="mt-10 flex flex-wrap gap-x-8 gap-y-3 border-t border-white/15 pt-6" data-testid="gates-badges">
              {c.badges.map((b) => (
                <li key={b} className="flex items-center gap-2 text-[13px] font-semibold text-white/85"><Check className="h-4 w-4 text-gold" /> {b}</li>
              ))}
            </ul>
          </div>
          <div className="relative min-h-[320px] lg:col-span-6">
            <img src="/images/porti-hero.jpg" alt={product.name} className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0b1220] via-[#0b1220]/30 to-transparent lg:via-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0b1220]/70 to-transparent lg:hidden" />
          </div>
        </div>
      </section>

      <section className="border-b border-ink/10 bg-surface" data-testid="gates-types">
        <div className={WIDE}>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div><p className="kicker">{c.types_k}</p><h2 className={H2}>{c.types_t}</h2></div>
            <p className="max-w-md text-[15px] text-ink/65 lg:text-right">{c.types_p}</p>
          </div>
          <div className="mt-12 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {c.types.map((tp, i) => (
              <Reveal key={tp.t} delay={i * 0.08}>
                <article className="group flex h-full flex-col border border-ink/10 bg-base transition-shadow duration-500 hover:shadow-2xl hover:shadow-black/10" data-testid={`gates-type-${i}`}>
                  <div className="relative h-56 overflow-hidden bg-[#0b1220]">
                    <img src={tp.img} alt={tp.t} className="h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-[1.05]" loading="lazy" />
                    <span className="absolute left-5 top-5 font-mono text-[11px] tracking-[0.25em] text-white/80">0{i + 1}</span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-heading text-xl font-semibold text-ink">{tp.t}</h3>
                    <p className="mt-3 text-[14px] leading-relaxed text-ink/65">{tp.x}</p>
                    <ul className="mt-5 space-y-2">
                      {tp.items.map((it) => (
                        <li key={it} className="flex items-start gap-2 text-[13px] text-ink/80"><span className="mt-2 h-1 w-1 shrink-0 bg-gold" aria-hidden /> {it}</li>
                      ))}
                    </ul>
                    <button onClick={() => scrollTo("gates-form")} data-testid={`gates-type-cta-${i}`} className="mt-auto inline-flex items-center gap-2 pt-6 text-[13px] font-semibold text-ink underline decoration-gold decoration-2 underline-offset-[6px] transition-colors hover:text-gold">
                      {c.cta_k} <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="gates-dev" className="relative scroll-mt-24 overflow-hidden border-b border-ink/10 bg-[#0b1220] text-white" data-testid="gates-dev">
        <img src="/images/porti-2.jpg" alt="" aria-hidden className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.14]" />
        <div className={`${WIDE} relative grid gap-12 lg:grid-cols-12`}>
          <div className="lg:col-span-4">
            <p className="kicker text-white/60">{c.dev_k}</p>
            <h2 className={`${H2} text-white`}>{c.dev_t}</h2>
            <p className="mt-6 text-[15px] leading-[1.7] text-white/65">{c.dev_p}</p>
            <button onClick={() => scrollTo("gates-form")} data-testid="gates-dev-cta" className="mt-10 inline-flex items-center gap-2 bg-white px-6 py-3.5 text-[13px] font-semibold text-ink transition-colors hover:bg-gold">
              {c.cta_k} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <ol className="grid gap-px bg-white/10 sm:grid-cols-2 lg:col-span-8 lg:grid-cols-3">
            {c.dev.map(([t, x], i) => (
              <li key={t} className="group bg-[#0b1220]/90 p-8 backdrop-blur-sm">
                <span className="font-mono text-[12px] tracking-[0.25em] text-white/40 transition-colors group-hover:text-gold">0{i + 1}</span>
                <h3 className="mt-6 text-[17px] font-bold text-white">{t}</h3>
                <p className="mt-3 text-[15px] leading-[1.6] text-white/65">{x}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="border-b border-ink/10" data-testid="gates-materials">
        <div className={`${WIDE} grid gap-12 lg:grid-cols-12`}>
          <div className="lg:col-span-5">
            <p className="kicker">{c.mat_k}</p>
            <h2 className={H2}>{c.mat_t}</h2>
            <p className="mt-6 text-[15px] leading-[1.7] text-ink/65">{c.mat_p}</p>
            <p className="mt-10 text-[11px] font-semibold uppercase tracking-[0.25em] text-ink/50">{c.ral_label}</p>
            <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-6 lg:grid-cols-3" data-testid="gates-ral">
              {c.ral.map(([hex, code, name]) => (
                <div key={code} className="border border-ink/10 bg-base p-2">
                  <div className="h-12 w-full" style={{ backgroundColor: hex }} />
                  <p className="mt-2 text-[11px] font-semibold text-ink">{code}</p>
                  <p className="text-[10px] text-ink/50">{name}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:col-span-7">
            {c.materials.map(([t, x], i) => (
              <div key={t} className="bg-base p-8">
                <span className="font-mono text-[12px] tracking-[0.25em] text-ink/40">0{i + 1}</span>
                <h3 className="mt-6 text-[17px] font-bold tracking-tight text-ink">{t}</h3>
                <p className="mt-3 text-[15px] leading-[1.6] text-ink/65">{x}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="gates-dims" className="scroll-mt-24 border-b border-ink/10 bg-surface" data-testid="gates-dims">
        <div className={WIDE}>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div><p className="kicker">{c.dim_k}</p><h2 className={H2}>{c.dim_t}</h2></div>
            <p className="max-w-lg text-[15px] text-ink/65 lg:text-right">{c.dim_p}</p>
          </div>
          <div className="mt-12 overflow-x-auto border border-ink/10">
            <table className="w-full min-w-[860px] text-left" data-testid="gates-dims-table">
              <thead>
                <tr className="bg-[#0b1220] text-white">
                  {c.dim_head.map((h) => <th key={h} className="px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.2em]">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {c.dims.map((row, i) => (
                  <tr key={row[0]} className={`border-t border-ink/10 ${i % 2 ? "bg-surface" : "bg-base"}`}>
                    {row.map((cell, j) => (
                      <td key={j} className={`px-6 py-5 text-[15px] ${j === 0 ? "font-semibold text-ink" : j === 4 ? "text-ink/65" : "font-mono text-ink/75"}`}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-[13px] text-ink/50">{c.dim_note}</p>
        </div>
      </section>

      <section className="border-b border-ink/10" data-testid="gates-kit">
        <div className={`${WIDE} grid gap-12 lg:grid-cols-12`}>
          <div className="lg:col-span-4">
            <p className="kicker">{c.kit_k}</p>
            <h2 className={H2}>{c.kit_t}</h2>
            <p className="mt-6 text-[15px] leading-[1.7] text-ink/65">{c.kit_p}</p>
          </div>
          <ol className="grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:col-span-8">
            {c.kit.map(([t, x], i) => (
              <li key={t} className="group bg-base p-8">
                <span className="font-mono text-[12px] tracking-[0.25em] text-ink/40 transition-colors group-hover:text-gold">0{i + 1}</span>
                <h3 className="mt-6 text-[17px] font-bold text-ink">{t}</h3>
                <p className="mt-3 text-[15px] leading-[1.6] text-ink/65">{x}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="border-b border-ink/10 bg-[#0b1220] text-white" data-testid="gates-process">
        <div className={WIDE}>
          <p className="kicker text-white/60">{c.proc_k}</p>
          <h2 className={`${H2} text-white`}>{c.proc_t}</h2>
          <div className="mt-12 grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {c.steps.map(([t, x], i) => (
              <div key={t} className="group bg-[#0b1220] p-8 lg:p-10">
                <span className="font-heading text-5xl font-semibold text-white/15 transition-colors duration-500 group-hover:text-gold">0{i + 1}</span>
                <h3 className="mt-8 text-[17px] font-bold text-white">{t}</h3>
                <p className="mt-3 text-[15px] leading-[1.6] text-white/65">{x}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="gates-specs" className="scroll-mt-24 border-b border-ink/10 bg-surface" data-testid="gates-specs">
        <div className={`${WIDE} grid gap-12 lg:grid-cols-12`}>
          <div className="lg:col-span-4"><p className="kicker">{c.spec_k}</p><h2 className={H2}>{c.spec_t}</h2></div>
          <dl className="border-t border-ink/15 lg:col-span-8">
            {c.specs.map(([k, v]) => (
              <div key={k} className="grid gap-2 border-b border-ink/10 py-5 sm:grid-cols-12">
                <dt className="text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/50 sm:col-span-4 sm:pt-1">{k}</dt>
                <dd className="text-[16px] font-medium text-ink sm:col-span-8">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="border-b border-ink/10" data-testid="gates-faq">
        <div className={`${WIDE} grid gap-12 lg:grid-cols-12`}>
          <div className="lg:col-span-4"><p className="kicker">{c.faq_k}</p><h2 className={H2}>{c.faq_t}</h2></div>
          <div className="border-t border-ink/10 lg:col-span-8">
            {c.faq.map(([q, a], i) => (
              <details key={q} className="group border-b border-ink/10" data-testid={`gates-faq-${i}`}>
                <summary className="flex cursor-pointer list-none items-center justify-between gap-6 py-5 text-[17px] font-semibold text-ink [&::-webkit-details-marker]:hidden">
                  {q}<span className="shrink-0 font-mono text-xl text-ink/40 transition-transform duration-300 group-open:rotate-45">+</span>
                </summary>
                <p className="pb-6 text-[15px] leading-[1.7] text-ink/65">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-ink/10 bg-surface">
        <div className={`${WIDE} !py-14`}>
          <Link to="/produse/carport" data-testid="gates-carport-link" className="group flex flex-col gap-6 border border-ink/10 bg-base p-8 transition-colors hover:border-ink/30 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-ink/50">{c.cross_k}</p>
              <h3 className="mt-2 font-heading text-2xl font-semibold text-ink">{c.cross_t}</h3>
              <p className="mt-2 max-w-xl text-[14px] text-ink/65">{c.cross_x}</p>
            </div>
            <span className="flex h-12 w-12 shrink-0 items-center justify-center border border-ink/20 text-ink transition-colors group-hover:bg-ink group-hover:text-white"><ArrowUpRight className="h-5 w-5" /></span>
          </Link>
        </div>
      </section>

      <section id="gates-form" className="scroll-mt-24">
        <div className={`${WIDE} grid gap-16 lg:grid-cols-2`}>
          <div>
            <p className="kicker">{c.cta_k}</p>
            <h2 className={`${H2} leading-[1.1]`}>{c.cta_t}</h2>
            <p className="mt-6 max-w-md text-[15px] leading-[1.7] text-ink/65">{c.cta_p}</p>
          </div>
          <Reveal delay={0.1}><ContactForm defaultSubject={c.form_subject} /></Reveal>
        </div>
      </section>
    </div>
  );
}
